import { useEffect } from 'react';
import { useForecastMap, mapAlive } from '../ForecastMapContext';
import { windTileTemplate } from '../../../lib/wind/urls';
import { paramForLevel } from '../../../lib/wind/levels';
import { buildParticleImage } from '../../../lib/wind/streamsImage';
import { buildTerrainSampler, terrainMaskZoom } from '../../../lib/wind/terrainSampler';
import { WIND_LEGEND_STOPS } from '../../../lib/wind/palette';
import { getDecodedTile } from './tileCache';

// Spec 2: animated particle streams via WeatherLayers GL (deck.gl MapboxOverlay).
// deck.gl + weatherlayers-gl are LAZY-imported so they live in their own chunk
// and only download the first time Streams mode is switched on (bundle gate).
//
// The particle image is a coarse regional uv field (z3–6 tiles, viewport + 1
// tile padding, mercator→equirect resampled in buildParticleImage) — particles
// advect a smoothed field; they don't need the fill's resolution.

const MAX_TILES = 36;

// Particle look: the WeatherLayers "modern streams" style (Milan's pick over
// Skysight's plain black) — particles coloured by LOCAL WIND SPEED using the
// same ramp as the legend/fill, darkened so they pop against the pastel fill.
// numParticles is NOT here — it scales with the field area per refresh (see
// particleCount), so streams stay visible when zoomed in.
const PARTICLE_PROPS = {
  // Trail length ≈ speedFactor × maxAge: speedFactor sets per-frame movement
  // (the perceived wind speed), maxAge how many frames of path stay visible.
  // Milan: 4 was too slow at ~10 m/s; 10 + the old maxAge 80 overstretched.
  maxAge: 30,
  speedFactor: 10,
  width: 1.8,
  opacity: 0.9,
};

// The built field spans whole wind tiles (z≤7), so it's always far larger than a
// zoomed-in viewport. We CROP it to the viewport (+ margin for pan tolerance)
// before handing it to the ParticleLayer, so the field ≈ what's on screen and a
// fixed particle count then gives ~constant on-screen density at every zoom.
// (A fixed 1500 over the old uncropped ~continental field left <1 in view.)
const STREAM_PARTICLES = 1800; // tune for density
const CROP_MARGIN = 0.35;      // fraction of the viewport added each side

// Crop an equirect field image to a lng/lat box. Bounds are linear in lng/lat,
// so this is plain pixel-rect math; returns the same {data,width,height,bounds,
// imageUnscale} shape over the (clamped) box.
const cropField = (img, west, south, east, north) => {
  const [W, S, E, N] = img.bounds;
  const x0 = Math.max(0, Math.floor(((west - W) / (E - W)) * img.width));
  const x1 = Math.min(img.width, Math.ceil(((east - W) / (E - W)) * img.width));
  const y0 = Math.max(0, Math.floor(((N - north) / (N - S)) * img.height));
  const y1 = Math.min(img.height, Math.ceil(((N - south) / (N - S)) * img.height));
  const cw = x1 - x0, ch = y1 - y0;
  if (cw <= 0 || ch <= 0) return img; // viewport outside the field → keep as-is
  const data = new Uint8ClampedArray(cw * ch * 4);
  for (let yy = 0; yy < ch; yy++) {
    const srcStart = ((y0 + yy) * img.width + x0) * 4;
    data.set(img.data.subarray(srcStart, srcStart + cw * 4), yy * cw * 4);
  }
  return {
    data, width: cw, height: ch,
    bounds: [
      W + (x0 / img.width) * (E - W), N - (y1 / img.height) * (N - S),
      W + (x1 / img.width) * (E - W), N - (y0 / img.height) * (N - S),
    ],
    imageUnscale: img.imageUnscale,
  };
};

// cpt2js palette: [speed m/s, color] stops over 0..maxSpeed (the product's
// legend scale), from the shared legend ramp darkened for contrast.
const DARKEN = 0.65;
const particlePalette = (maxSpeed) => WIND_LEGEND_STOPS.map((c, i) => [
  (i / (WIND_LEGEND_STOPS.length - 1)) * maxSpeed,
  `rgb(${Math.round(c[0] * DARKEN)}, ${Math.round(c[1] * DARKEN)}, ${Math.round(c[2] * DARKEN)})`,
]);

// Field zoom tracks the map zoom (kept a couple levels coarse for smooth
// advection) so the field stays near the viewport instead of spanning the
// continent — that, with particleCount scaling, is what keeps streams visible
// when zoomed in.
const fieldZoom = (mapZoom) => Math.max(4, Math.min(9, Math.round(mapZoom) - 2));

const lngLatToTile = (lng, lat, z) => {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n);
  return [Math.max(0, Math.min(n - 1, x)), Math.max(0, Math.min(n - 1, y))];
};

const WindStreamsLayer = ({ base, date, time, product, level, model }) => {
  const map = useForecastMap();
  const param = product && (product.levelStepped ? paramForLevel(level) : product.param);

  useEffect(() => {
    if (!map || !param || !date || !time) return undefined;
    let cancelled = false;
    let overlay = null;
    let timer = null;
    let detachMove = null;
    const controller = new AbortController();
    const range = product.range;
    const template = windTileTemplate({ base, date, time, param, model });

    // Terrain masking applies only to Wind-at-altitude, whose `level` is a real
    // altitude (metres). Other products have no single level → no mask.
    const maskLevel = product.levelStepped ? level : null;
    // Wind tiles only exist up to this zoom (composite-ss: uv_10→z9, rest→z7;
    // ICON→z7). Requesting finer tiles 404s → empty field, so cap the field zoom.
    const maxDataZoom = model === 'ICON' ? 7 : param === 'uv_10' ? 9 : 7;

    const buildImage = async () => {
      const z = Math.min(maxDataZoom, fieldZoom(map.getZoom()));
      const b = map.getBounds();
      // one tile of padding so panning doesn't immediately leave the field
      const [x0, y0] = lngLatToTile(b.getWest(), b.getNorth(), z);
      const [x1, y1] = lngLatToTile(b.getEast(), b.getSouth(), z);
      const n = Math.pow(2, z);
      const xMin = Math.max(0, Math.min(x0, x1) - 1);
      const xMax = Math.min(n - 1, Math.max(x0, x1) + 1);
      const yMin = Math.max(0, Math.min(y0, y1) - 1);
      const yMax = Math.min(n - 1, Math.max(y0, y1) + 1);
      const cols = xMax - xMin + 1, rows = yMax - yMin + 1;
      if (cols * rows > MAX_TILES) return null;
      const tiles = new Map();
      await Promise.all(
        Array.from({ length: cols * rows }, (_, k) => {
          const x = xMin + (k % cols), y = yMin + Math.floor(k / cols);
          const url = template.replace('{z}', z).replace('{x}', x).replace('{y}', y);
          return getDecodedTile(url, controller.signal)
            .then((tile) => { if (tile) tiles.set(`${x}/${y}`, tile); })
            .catch(() => {});
        }),
      );
      if (cancelled || tiles.size === 0) return null;
      let maskAbove = null;
      if (maskLevel != null) {
        const b2 = map.getBounds();
        const sampler = await buildTerrainSampler({
          getTile: getDecodedTile, base, z: terrainMaskZoom(map.getZoom()),
          west: b2.getWest(), south: b2.getSouth(), east: b2.getEast(), north: b2.getNorth(),
          signal: controller.signal,
        });
        if (cancelled) return null;
        maskAbove = (lng, lat) => sampler.aboveLevel(lng, lat, maskLevel);
      }
      const img = buildParticleImage({
        getTile: (x, y) => tiles.get(`${x}/${y}`) || null,
        z, xMin, xMax, yMin, yMax, range, maskAbove,
      });
      // Crop to the viewport (+ margin) so particles concentrate where the user
      // is looking instead of being spread across the whole tile field.
      const mLng = (b.getEast() - b.getWest()) * CROP_MARGIN;
      const mLat = (b.getNorth() - b.getSouth()) * CROP_MARGIN;
      return cropField(img, b.getWest() - mLng, b.getSouth() - mLat, b.getEast() + mLng, b.getNorth() + mLat);
    };

    (async () => {
      const [{ MapboxOverlay }, WeatherLayers] = await Promise.all([
        import('@deck.gl/mapbox'),
        import('weatherlayers-gl'),
      ]);
      if (cancelled) return;

      const maxSpeed = (product.fillRange && product.fillRange.max) || 12;
      const makeLayer = (img) => new WeatherLayers.ParticleLayer({
        id: 'wind:streams:particles',
        image: { data: img.data, width: img.width, height: img.height },
        imageType: WeatherLayers.ImageType.VECTOR,
        imageUnscale: img.imageUnscale,
        bounds: img.bounds,
        animate: true,
        palette: particlePalette(maxSpeed),
        numParticles: STREAM_PARTICLES,
        ...PARTICLE_PROPS,
      });

      const refresh = async () => {
        const img = await buildImage();
        if (cancelled || !img) return;
        if (!overlay) {
          overlay = new MapboxOverlay({ interleaved: false, layers: [makeLayer(img)] });
          map.addControl(overlay);
        } else {
          overlay.setProps({ layers: [makeLayer(img)] });
        }
      };

      const onMoveEnd = () => {
        clearTimeout(timer);
        timer = setTimeout(refresh, 300);
      };

      await refresh();
      if (cancelled) return;
      map.on('moveend', onMoveEnd);
      detachMove = () => map.off('moveend', onMoveEnd);
    })();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
      if (detachMove) detachMove();
      if (overlay) {
        if (mapAlive(map)) map.removeControl(overlay);
        else overlay.finalize && overlay.finalize(); // free deck GL resources
        overlay = null;
      }
    };
  }, [map, base, date, time, param, product, level, model]);

  return null;
};

export default WindStreamsLayer;
